import * as React from 'react';
import { I18n } from 'react-redux-i18n';
import last from 'lodash/last';
import cx from 'classnames';
import isEmpty from 'lodash/isEmpty';

import { SORT_DIRECTION } from '@common/constants';
import { isNumber } from '@common/utils/string';
import { downloadFile } from '@common/utils/file';
import { getFields, updateField, deleteRow, createRow, exportData, importData } from './actions/fields';
import { LOOKUP_TABLE_FIELD_TYPE } from './const/fields';
import { defaultRender } from '@common/utils/dom';
import { Table, getScrollableElement } from '@common/components/Table';
import { Checkbox } from './components/Checkbox';
import { Date } from './components/Date';
import { Dropdown } from './components/Dropdown';
import { HyperLink } from './components/HyperLink';
import { Image } from './components/Image';
import { Text } from './components/Text';
import { Input } from './components/Input';
import { DataExport } from './components/DataExport';
import { DataImport } from './components/DataImport';
import { Flex } from '@common/components/Flex';
import { Button } from '@common/components/Button';
import { MessageBox } from '@common/components/MessageBox';
import { Loading } from '@common/components/loading';
import { getCookie, setCookie } from '@common/utils/cookie';

import './LookupTable.styles.scss';

export const LOOKUP_MODULE_NAME = 'Lookup-table';
const INITIAL_DATA_WHILE_CREATING_VALUE = undefined;

export class LookupTable extends React.Component {
  rootRef = React.createRef();
  sortingStorageKey = 'lookup-table-sorting';

  saveSortingToStorage = (sorting) => {
    setCookie(this.sortingStorageKey, sorting);
  }

  getSortingFromStorage = () => {
    return getCookie(this.sortingStorageKey) || {};
  }

  state = {
    dataExportOpened: false,
    dataImportOpened: false,
    messageProps: {
      header: '',
      text: '',
      show: false,
      confirmText: 'Ok',
      cancelText: '',
      size: 'medium',
      onCustomOk: () => {},
    },
    loadMetadata: {
      pagination: {}, 
      sorting: this.getSortingFromStorage(),
      filtering: {},
    },
    dataWhileCreatingRow: INITIAL_DATA_WHILE_CREATING_VALUE,
    creatingRow: {},
    creatingRowId: undefined,
    selectedRow: undefined,
    submittingCreatingRow: false,
    data: [],
    columns: [],
    fields: {},
  };

  rowAttribute = 'data-id';

  get commonFieldProps() {
    return {
      className: 'LookupTable__field',
      onSave: this.handleSave,
    };
  }

  getRowId = (item) => {
    return item[this.mainColumn.id];
  }

  getTrProps = (state, { row }) => {
    return {
      [this.rowAttribute]: this.getRowId(row)
    };
  }

  normalizeNumberBeforeSave = (value) => {
    if (last(value) === '.') {
      value = value.trim().slice(0, -1);
    }
  
    return value;
  }
  
  renderNumber = (data) => {
    return (
      <Input
        normalizeBeforeSave={this.normalizeNumberBeforeSave}
        validateValue={isNumber}
        {...data}
        {...this.commonFieldProps}
      />
    );
  }

  renderInput = (data) => {
    return <Input {...data} {...this.commonFieldProps} />;
  }

  renderText = (data) => {
    return <Text {...data} {...this.commonFieldProps} />;
  }

  renderDate = (data) => {
    return <Date {...data} {...this.commonFieldProps} />;
  }

  renderCheckbox = (data) => {
    return <Checkbox {...data} {...this.commonFieldProps} />;
  }

  renderHyperLink = (data) => {
    return <HyperLink {...data} {...this.commonFieldProps} />;
  }

  renderDropdown = (data) => {
    return <Dropdown {...data} {...this.commonFieldProps} />;
  }

  renderImage = (data) => {
    return <Image {...data} {...this.commonFieldProps} />;
  }

  renderField = (fieldType, data) => {
    const fieldConfig = this.fieldMetadata[fieldType];
    const render = fieldConfig ? fieldConfig.render : defaultRender;

    return render(data);
  }

  handleSave = async ({ index, id, value, savedValue, metadata }) => {
    const isMainColumn = id == this.mainColumn.id;

    try {
      if (metadata.creating) {
        if (this.state.submittingCreatingRow) {
          return;
        }

        if (isMainColumn) {
          await this.createRow({ value });
        } else {
          this.updateCreatingRow({ [id]: value });
        }
      } else {
        await this.updateField({ index, id, value, savedValue, fields: this.state.fields }); 
        this.loadData();
      }
    } catch (err) {
      let errorText = err?.entity?.errorMessage;

      if (!errorText) {
        errorText = metadata.creating
          ? I18n.t('Failed to create field')
          : I18n.t('Failed to update row');
      }

      if (isMainColumn && !value) {
        errorText = I18n.t('This column cannot have an empty field');
      }

      this.setMessageProps({
        header: I18n.t('Warning'),
        text: errorText,
        show: true,
        confirmText: 'Ok',
        cancelText: '',
        size: 'medium',
        onCustomOk: this.closeMessage,
      });

      console.error(err);

      throw err;
    }
  }

  updateCreatingRow = (data) => {
    this.setState((state) => ({
      ...state,
      creatingRow: {
        ...state.creatingRow,
        ...data
      }
    }));
  }

  createRow = async ({ value }) => {
    try {
      if (!value) {
        this.setMessageProps({
          header: I18n.t('Warning'),
          text: I18n.t('Enter the cell value to add a row'),
          show: true,
          confirmText: 'Ok',
          cancelText: '',
          size: 'medium',
          onCustomOk: this.closeMessage,
        });
  
        return;
      }
  
      this.setState((state) => ({ ...state, submittingCreatingRow: true, dataWhileCreatingRow: this.state.data }));
  
      await createRow({
        primaryCell: {
          id: this.mainColumn.id,
          value,
        },
        fields: this.state.fields,
        columns: this.state.columns,
      });
      const { data, fields } = await this.loadData();

      const getUpdatedRowIndex = (items) => {
        return items.findIndex((row) => {
          return row[this.mainColumn.id] === value;
        });
      }
  
      let updatedIndex = getUpdatedRowIndex(data);
  
      const updatedRow = Object.keys(this.state.creatingRow).reduce((values, columnId) => {
        if (columnId == this.mainColumn.id) {
          return values;
        }
  
        const cellValue = this.state.creatingRow[columnId];
  
        return [...values, [columnId, cellValue]];
      }, []);
  
      for (const [columnId, cellValue] of updatedRow) {
        await this.updateField({ index: updatedIndex, id: columnId, value: cellValue, fields });
        const { data } = await this.loadData();
        updatedIndex = getUpdatedRowIndex(data);
      }
  
      this.resetCreatingRow();
      this.setSelectedRow({
        [this.mainColumn.id]: value
      });
      this.setState((state) => ({ ...state, submittingCreatingRow: false, dataWhileCreatingRow: INITIAL_DATA_WHILE_CREATING_VALUE }));
      const rerenderTime = 300;
      await new Promise((r) => setTimeout(r, rerenderTime));
      this.scrollToCreatedRow({ [this.mainColumn.id]: value });
    } catch (err) {
      this.setMessageProps({
        header: I18n.t('Warning'),
        text: err?.entity || I18n.t('Failed to create row'),
        show: true,
        confirmText: 'Ok',
        cancelText: '',
        size: 'medium',
        onCustomOk: this.closeMessage,
      });
      console.error(err);
    }
  }

  scrollToCreatedRow = (item) => {
    const row = this.rootRef.current.querySelector((`[${this.rowAttribute}="${this.getRowId(item)}"]`));

    row?.scrollIntoView({ block: 'end', inline: 'nearest' });
  }

  updateField = ({ index, id, value, savedValue, fields }) => {
    if (value == savedValue) {
      return;
    }

    return updateField({ 
      index, 
      id, 
      value, 
      fields,
    });
  }

  get fieldMetadata() {
    return {
      [LOOKUP_TABLE_FIELD_TYPE.system]: {
        render: this.renderText
      },
      [LOOKUP_TABLE_FIELD_TYPE.text]: {
        render: this.renderInput
      },
      [LOOKUP_TABLE_FIELD_TYPE.date]: {
        render: this.renderDate
      },
      [LOOKUP_TABLE_FIELD_TYPE.number]: {
        render: this.renderNumber
      },
      [LOOKUP_TABLE_FIELD_TYPE.checkbox]: {
        render: this.renderCheckbox
      },
      [LOOKUP_TABLE_FIELD_TYPE.dropdown]: {
        render: this.renderDropdown
      },
      [LOOKUP_TABLE_FIELD_TYPE.image]: {
        render: this.renderImage
      },
      [LOOKUP_TABLE_FIELD_TYPE.lookupTable]: {
        render: this.renderInput
      },
      [LOOKUP_TABLE_FIELD_TYPE.table]: {
        render: this.renderText
      },
      [LOOKUP_TABLE_FIELD_TYPE.computed]: {
        render: this.renderText
      },
      [LOOKUP_TABLE_FIELD_TYPE.summary]: {
        render: this.renderText
      },
      [LOOKUP_TABLE_FIELD_TYPE.tableSummary]: {
        render: this.renderText
      },
      [LOOKUP_TABLE_FIELD_TYPE.hyperlink]: {
        render: this.renderHyperLink
      },
    };
  }

  renderCountCell = ({ viewIndex }) => {
    return viewIndex + 1;
  }

  get mainColumn() {
    return this.state.columns.length ? this.state.columns[0] : undefined;
  }

  getColumnId = (column) => {
    return String(column.id);
  }

  get columns() {
    const countColumn = {
      resizable: false,
      Header: '', 
      Cell: this.renderCountCell,
      className: 'LookupTable__count-field',
      minWidth: 40,
      width: 40,
      accessor: 'count-column'
    };
    const widthMap = {
      [LOOKUP_TABLE_FIELD_TYPE.date]: 160
    };

    const columns = this.state.columns.map((column) => {
      const width = widthMap[column.type] || 130;
      const columnId = this.getColumnId(column);

      return {
        sortable: true,
        Header: column.name, 
        Cell: ({ original, index }) => {
          const isCreatingRow = this.state.creatingRowId && original.id === this.state.creatingRowId;
          const valueNormalizerMap = {
            [LOOKUP_TABLE_FIELD_TYPE.number]: (value) => {
              return value ? Number(value).toString() : value;
            }
          };
          const normalizeValue = valueNormalizerMap[column.type] || ((x) => x);
          const normalizedValue = normalizeValue(original[columnId]);
          const normalizedDefaultValue = normalizeValue(column.defaultValue || '');
          
          const fieldData = {
            index,
            options: column.fieldOptions,
            value: normalizedValue,
            defaultValue: normalizedDefaultValue,
            id: columnId,
            metadata: {
              creating: isCreatingRow,
            }
          };
          
          return this.renderField(column.type, fieldData);
        },
        accessor: columnId,
        minWidth: width,
        width,
        className: 'table-actions-cell'
      };
    });

    return [
      countColumn,
      ...columns
    ];
  }

  loadData = (params) => {
    const { tableMetadata } = this.props;
    const requestParams = {
      ...this.state.loadMetadata,
      ...params,
    };

    if (!isEmpty(params?.sorting)) {
      requestParams.updateSort = true;
      this.saveSortingToStorage(params.sorting);
    }

    return getFields(
      { 
        fieldId: tableMetadata.tableFieldId, 
        context: tableMetadata.tableFieldContext 
      }, 
      requestParams
    ).then((response) => {
      this.setState((x) => ({
        ...x,
        ...response,
      }));

      return response;
    });
  }

  setLoadMetadata = (metadata = {}) => {
    this.setState((state) => ({
      ...state,
      loadMetadata: {
        ...this.state.loadMetadata,
        ...metadata,
      }
    }));
  }

  handleRowAdd = () => {
    this.setState(
      (state) => ({
        ...state,
        creatingRowId: 'creating-row',
      }),
      () => {
        const scrollableContentElement = getScrollableElement(this.rootRef.current);
    
        scrollableContentElement.scrollTop = scrollableContentElement.scrollHeight;
        scrollableContentElement.scrollLeft = 0;
      }
    );
  }

  handleRowDelete = async () => {
    this.resetSelectedRow();

    await deleteRow({
      fields: this.state.fields,
      primaryCell: {
        id: this.mainColumn.id,
        value: this.state.selectedRow[this.mainColumn.id]
      }
    });
    this.loadData();
  }
  
  openTableExport = () => {
    this.setState((state) => ({ ...state, dataExportOpened: true }));
  }

  closeTableExport = () => {
    this.setState((state) => ({ ...state, dataExportOpened: false }));
  }

  openTableImport = () => {
    this.setState((state) => ({ ...state, dataImportOpened: true }));
  }

  closeTableImport = () => {
    this.setState((state) => ({ ...state, dataImportOpened: false }));
  }

  get actions() {
    return [
      {
        iconSrc: 'gif/questionnaire_add.gif',
        disabled: Boolean(this.state.creatingRowId),
        label: 'Add Row',
        onClick: this.handleRowAdd
      },
      {
        iconSrc: 'gif/questionnaire_delete.gif',
        disabled: !this.state.selectedRow,
        label: 'Delete Row',
        onClick: this.handleRowDelete
      },
      {
        iconSrc: 'gif/ss_export.gif',
        label: 'Export',
        disabled: !this.state?.data?.length,
        onClick: this.openTableExport
      },
      {
        iconSrc: 'gif/ss_import.gif',
        label: 'Import',
        onClick: this.openTableImport
      },
    ].map((x) => ({ ...x, label: I18n.t(x.label) }));
  }

  setMessageProps = (props) => {
    this.setState((state) => ({
      ...state,
      messageProps: {
        size: 'medium',
        ...props,
      },
    }));
  }

  closeMessage = () => {
    this.setMessageProps({ show: false });
  }

  setSelectedRow = (selectedRow) => {
    if (this.state.creatingRowId && selectedRow.id === this.state.creatingRowId) {
      return;
    }

    this.setState((state) => ({ ...state, selectedRow }));
  }

  resetSelectedRow = () => {
    this.setState((state) => ({ ...state, selectedRow: undefined }));
  }

  resetCreatingRow = () => {
    this.setState((state) => ({ ...state, creatingRowId: undefined, creatingRow: {} }));
  }

  get tableIdProperty() {
    return this.mainColumn ? this.mainColumn.id : undefined;
  }

  get data() {
    if (this.state.dataWhileCreatingRow) {
      return this.state.dataWhileCreatingRow;
    }

    if (this.state.creatingRowId) {
      const baseData = [...this.state.data];
      const creatingRow = {
        [this.tableIdProperty]: '',
        id: this.state.creatingRowId,
      };

      baseData.push(creatingRow);

      return baseData;
    }

    return this.state.data;
  }

  handleExportData = async (data) => {
    try {
      const { tableMetadata } = this.props;

      const { entity, headers } = await exportData({
        fieldId: tableMetadata.tableFieldId,
        context: tableMetadata.tableFieldContext,
        ...data
      });

      const filename = headers['content-disposition'].split('= ')[1];

      downloadFile(entity, filename, headers['content-type']);

      this.closeTableExport();
    } catch (err) {
      this.setMessageProps({
        header: I18n.t('Warning'),
        text: I18n.t('Failed to export data'),
        show: true,
        confirmText: 'Ok',
        cancelText: '',
        size: 'medium',
        onCustomOk: this.closeMessage,
      });
      console.error(err);
    }
  }

  handleImportData = async (data) => {
    try {
      const { tableMetadata } = this.props;

      const response = await importData({
        fieldId: tableMetadata.tableFieldId,
        context: tableMetadata.tableFieldContext,
        ...data
      });

      const { entity } = response;

      if (entity?.payLoad?.processed > 0) {
        this.loadData();
      }

      if (entity?.errorMessage) {
        return {
          entity: {
            payLoad: {
              errors: [entity?.errorMessage]
            }
          }
        };
      }

      return response;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  get sortedColumn() {
    const { loadMetadata } = this.state;

    if (!isEmpty(loadMetadata.sorting)) {
      return loadMetadata.sorting;
    }

    const { columns } = this.state;
    const sortedColumnIndex = columns.findIndex(({ sortOrder }) => sortOrder);
    const sortedColumn = columns[sortedColumnIndex];

    if (sortedColumn) {
      const nonConsideringCountColumn = 1;

      return {
        direction: sortedColumn.ascdsc === 0 ? SORT_DIRECTION.desc : SORT_DIRECTION.asc,
        columnIndex: sortedColumnIndex + nonConsideringCountColumn,
      };
    }

    return {};
  }

  render() {
    const { selectedRow, dataExportOpened, dataImportOpened, submittingCreatingRow } = this.state;

    return (
      <div className="LookupTable" ref={this.rootRef}>
        {dataExportOpened && (
          <DataExport 
            close={this.closeTableExport}
            onExport={this.handleExportData}
          />
        )}
        {dataImportOpened && (
          <DataImport 
            close={this.closeTableImport}
            onImport={this.handleImportData}
          />
        )}
        <MessageBox
          hideMessageBox={this.closeMessage}
          {...this.state.messageProps}
        />
        <Flex alignItems="center" className="LookupTable__actions">
          {this.actions.map(({ iconSrc, label, ...btnProps }) => (
            <Button 
              key={label}
              id={`${LOOKUP_MODULE_NAME}-action-${label.toLowerCase().replace(' ', '-')}`} 
              className="LookupTable__actions__btn" {...btnProps}
            >
              <img src={iconSrc} />
              {I18n.t(label)}
            </Button>
          ))}
          {submittingCreatingRow && <Loading size={16} className="LookupTable__loading" />}
        </Flex>
        <Table
          serverSorting
          selectedItems={selectedRow ? [selectedRow] : []}
          idProperty={this.tableIdProperty}
          className={cx('LookupTable__table', { ['LookupTable__table--disabled']: submittingCreatingRow })}
          config={{
            data: this.data,
            columns: this.columns,
            getTrProps: this.getTrProps,
          }}
          loadMetadata={{
            sorting: this.sortedColumn
          }}
          onRowClick={this.setSelectedRow}
          onLoadData={this.loadData}
          setLoadMetadata={this.setLoadMetadata}
        />
      </div>
    );
  }
}